import { Navigation, Square, Clock, WifiOff, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

// Single source of truth for vehicle status visuals. Never color-only:
// every status carries an icon + label.
//
// `text`/`soft` carry a light-theme shade as the base color plus a `dark:`
// override back to the original pastel-on-near-black look — the pastel
// weight (300/400) reads fine on the dark theme's near-black background but
// washes out to near-invisible on the light theme's white one.
export const STATUS = {
  moving: { label: "Moving", icon: Navigation, dot: "bg-emerald-400", text: "text-emerald-600 dark:text-emerald-400", ring: "ring-emerald-400/30", soft: "bg-emerald-400/10 text-emerald-700 dark:text-emerald-300 border-emerald-400/20" },
  parked: { label: "Parked", icon: Square, dot: "bg-sky-400", text: "text-sky-600 dark:text-sky-400", ring: "ring-sky-400/30", soft: "bg-sky-400/10 text-sky-700 dark:text-sky-300 border-sky-400/20" },
  idle: { label: "Idle", icon: Clock, dot: "bg-amber-400", text: "text-amber-600 dark:text-amber-400", ring: "ring-amber-400/30", soft: "bg-amber-400/10 text-amber-700 dark:text-amber-300 border-amber-400/20" },
  offline: { label: "Offline", icon: WifiOff, dot: "bg-zinc-500", text: "text-zinc-600 dark:text-zinc-400", ring: "ring-zinc-500/30", soft: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20" },
  alert: { label: "Alert", icon: AlertTriangle, dot: "bg-red-500", text: "text-red-600 dark:text-red-400", ring: "ring-red-500/30", soft: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/25" },
};

export const SEVERITY = {
  critical: { label: "Critical", text: "text-red-600 dark:text-red-400", soft: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/25", dot: "bg-red-500" },
  warning: { label: "Warning", text: "text-amber-600 dark:text-amber-400", soft: "bg-amber-400/10 text-amber-700 dark:text-amber-300 border-amber-400/20", dot: "bg-amber-400" },
  info: { label: "Info", text: "text-sky-600 dark:text-sky-400", soft: "bg-sky-400/10 text-sky-700 dark:text-sky-300 border-sky-400/20", dot: "bg-sky-400" },
};

export function StatusBadge({ status, size = "md", className }) {
  const s = STATUS[status] || STATUS.offline;
  const Icon = s.icon;
  const sizes = size === "sm" ? "text-[11px] px-2 py-0.5 gap-1" : "text-xs px-2.5 py-1 gap-1.5";
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={cn("inline-flex items-center rounded-full border font-medium", s.soft, sizes, className)}
    >
      <Icon className={cn(size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5")} />
      {s.label}
    </span>
  );
}

export function SeverityBadge({ severity, className }) {
  const s = SEVERITY[severity] || SEVERITY.info;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium", s.soft, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
      {s.label}
    </span>
  );
}

export function StatusDot({ status, pulse }) {
  const s = STATUS[status] || STATUS.offline;
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      {pulse && status === "alert" && (
        <span className={cn("absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping-ring", s.dot)} />
      )}
      <span className={cn("relative inline-flex h-2.5 w-2.5 rounded-full", s.dot)} />
    </span>
  );
}
